import { ExpandLess, ExpandMore, Mail as MailIcon } from "@mui/icons-material";
import {
	List,
	ListSubheader,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Collapse,
	FormControl,
	InputLabel,
	Input,
	FormHelperText,
	ListItem,
	IconButton,
	ListItemAvatar,
	Avatar,
} from "@mui/material";
import DraftsIcon from "@mui/icons-material/Drafts";
import PersonIcon from "@mui/icons-material/Person";
import GroupIcon from "@mui/icons-material/AllInbox";
import VisibilityIcon from "@mui/icons-material/Visibility";
import React, { useEffect } from "react";
import {SEARCH_MAILS} from "../../graphql/queries/gqlSearchMails";
import { graphQLServices } from "../../../../services/GraphQLServices";
import { Mail } from "../../models/Mail";
import { MailSet } from "../../models/MailSet";
import { MailMetaDataDescription } from "../../models/MailMetaDataDescription";
import {
	getLatestStatus,
	isSuccessStatus,
	mapStatus,
} from "../../helpers/MailexStatusHelper";
import moment from "moment";
import {GenericModal} from "../GenericModal/GenericModal";
import {MailDescription} from "../MailDescription/MailDescription";
import {RelationshipDescription} from "../RelationshipDescription/RelationshipDescription";

export const MailexSearch = () => {
	const [openRelationships, setOpenRelationships] = React.useState(false);
	const [modalRelationshipId, setModalRelationshipId] =
		React.useState<string>();
	const [openMails, setOpenMails] = React.useState(false);
	const [modalMailId, setModalMailId] = React.useState<string>();
	const [openMailSets, setOpenMailSets] = React.useState(false);
	const [search, setSearch] = React.useState("");
	const [data, setData] = React.useState<Mail[]>([]);
	const [dataMailSets, setDataMailSets] = React.useState<MailSet[]>([]);
	const [dataRelationships, setDataRelationships] = React.useState<
		MailMetaDataDescription[]
	>([]);

	const fetch = async () => {
		const result = await graphQLServices.mailexClient.query({
			query: SEARCH_MAILS,
			variables: {
				search: search,
			},
		});

		const tempData = result?.data?.mails?.nodes ?? [];
		setData(tempData);
		setDataMailSets(getDistinctMailSets(tempData));
		setDataRelationships(getDistinctRelationships(tempData));
	};

	useEffect(() => {
		const timeOutId = setTimeout(() => {
			fetch();
		}, 500);
		return () => clearTimeout(timeOutId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [search]);

	const getDistinctMailSets = (data: Mail[]): MailSet[] => {
		if (data.length === 0) return [];

		return data
			.map((mail) => mail.mailSet)
			.filter(
				(value, index, self) =>
					index === self.findIndex((t) => t.id === value.id)
			);
	};

	const getDistinctRelationships = (
		data: Mail[]
	): MailMetaDataDescription[] => {
		if (data.length === 0) return [];

		return data
			.flatMap((mail) => mail.metaData.description)
			.filter(
				(value, index, self) =>
					index ===
					self.findIndex(
						(t) =>
							t.relationshipNumber === value.relationshipNumber &&
							t.relatedEmailAddress === value.relatedEmailAddress
					)
			);
	};

	return (
		<>
			<FormControl>
				<InputLabel required={true} htmlFor="description">
					Description
				</InputLabel>
				<Input
					id="description"
					aria-describedby="helper-description"
					value={search}
					required
					onChange={(event) => setSearch(event.target.value)}
				/>
				<FormHelperText id="helper-description">
					Search by email or relationship number.
				</FormHelperText>
			</FormControl>
			{(!data || data.length <= 0) && search &&
				<p>
					No Results Found.
				</p>
			}
			{data && data.length > 0 && (
				<List
					sx={{ width: "100%", maxWidth: 360, bgcolor: "background.paper" }}
					component="nav"
					aria-labelledby="nested-list-subheader"
					subheader={
						<ListSubheader component="div" id="nested-list-subheader">
							search results for {search}
						</ListSubheader>
					}
				>
					<ListItemButton
						onClick={() => setOpenRelationships(!openRelationships)}
					>
						<ListItemIcon>
							<PersonIcon />
						</ListItemIcon>
						<ListItemText primary="Relationships" />
						{openRelationships ? <ExpandLess /> : <ExpandMore />}
					</ListItemButton>
					<Collapse in={openRelationships} timeout="auto" unmountOnExit>
						<List component="div" disablePadding>
							{dataRelationships.map((relationship) => {
								return (
									<ListItem
										key={`search-user-${relationship.relationshipNumber}`}
										secondaryAction={
											<IconButton
												onClick={() =>
													setModalRelationshipId(
														relationship.relationshipNumber
													)
												}
												edge="end"
												aria-label="delete"
											>
												<VisibilityIcon />
											</IconButton>
										}
									>
										<ListItemAvatar>
											<Avatar>
												<PersonIcon />
											</Avatar>
										</ListItemAvatar>
										<ListItemText
											primary={`${relationship.relationshipNumber}`}
											secondary={`${relationship.titles} ${relationship.initials} ${relationship.name} (${relationship.relatedEmailAddress})`}
										/>
									</ListItem>
								);
							})}
						</List>
					</Collapse>

					<ListItemButton onClick={() => setOpenMails(!openMails)}>
						<ListItemIcon>
							<DraftsIcon />
						</ListItemIcon>
						<ListItemText primary="Mails" />
						{openMails ? <ExpandLess /> : <ExpandMore />}
					</ListItemButton>
					<Collapse in={openMails} timeout="auto" unmountOnExit>
						<List component="div" disablePadding>
							{data.map((mail) => {
								return (
									<ListItem
										key={`search-mail-${mail.id}`}
										secondaryAction={
											<IconButton
												onClick={() => setModalMailId(mail.id)}
												edge="end"
												aria-label="delete"
											>
												<VisibilityIcon />
											</IconButton>
										}
									>
										<ListItemAvatar>
											<Avatar>
												<MailIcon
													color={
														isSuccessStatus(getLatestStatus(mail))
															? "success"
															: "error"
													}
												/>
											</Avatar>
										</ListItemAvatar>
										<ListItemText
											primary={`${mail.mailSet.description}`}
											secondary={`${mapStatus(getLatestStatus(mail))} (${moment(
												mail.dateTimeStamp
											).format("ddd MMM D, yyyy HH:mm")})`}
										/>
									</ListItem>
								);
							})}
						</List>
					</Collapse>

					<ListItemButton onClick={() => setOpenMailSets(!openMailSets)}>
						<ListItemIcon>
							<GroupIcon />
						</ListItemIcon>
						<ListItemText primary="Mail Sets" />
						{openMailSets ? <ExpandLess /> : <ExpandMore />}
					</ListItemButton>
					<Collapse in={openMailSets} timeout="auto" unmountOnExit>
						<List component="div" disablePadding>
							{dataMailSets.map((mailset) => {
								return (
									<ListItem
										key={`search-mailset-${mailset.id}`}
										secondaryAction={
											<IconButton
												onClick={() => console.log("test")}
												edge="end"
												aria-label="delete"
											>
												<VisibilityIcon />
											</IconButton>
										}
									>
										<ListItemAvatar>
											<Avatar>
												<GroupIcon />
											</Avatar>
										</ListItemAvatar>
										<ListItemText
											primary={`${mailset.description}`}
											secondary={`${mailset.status} (${mailset.scheduledDateTime})`}
										/>
									</ListItem>
								);
							})}
						</List>
					</Collapse>
				</List>
			)}

			<GenericModal
				title={"Details:"}
				open={!!modalMailId}
				onClose={() => setModalMailId(undefined)}
			>
				{/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
				<MailDescription mailId={modalMailId!} />
			</GenericModal>

			<GenericModal
				title={"Details:"}
				open={!!modalRelationshipId}
				onClose={() => setModalRelationshipId(undefined)}
			>
				<RelationshipDescription metadata={dataRelationships.filter(it => it.relationshipNumber === modalRelationshipId)[0]} />
			</GenericModal>
		</>
	);
};
